import React, { Dispatch, SetStateAction } from 'react';

import { QAlert, QDatepicker, QFormControl, QInput, QSelect, QStack, QText, QTextarea } from '@qualio/ui-components';
import { useConfigsQuery } from 'hooks';
import { Document, File } from 'providers';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { EditFileCard, AddDocumentCard } from 'v2components';

type CreateAuditFormProps = { formik: any; setAddAuditSubmitButtonDisabled: Dispatch<SetStateAction<boolean>> };

const CreateAuditForm: React.FC<CreateAuditFormProps> = ({ formik, setAddAuditSubmitButtonDisabled }) => {
  const { t } = useTranslation();
  const { auditTypeOptions } = useConfigsQuery();

  const handleAppendFile = (fileType: File['type'], file?: File) => {
    if (fileType === 'QUALIO_DOC') {
      const documents = formik.values.documents || [];
      formik.setFieldValue('documents', [...documents, { file: { type: fileType, id: uuidv4() } }]);
    }
    if (fileType === 'FILE') {
      const documents = formik.values.documents || [];
      const newDocument: Omit<Document, 'required' | 'name'> = {
        file: {
          fileName: file?.fileName,
          id: file?.id as string,
          type: 'FILE',
        },
      };
      formik.setFieldValue('documents', [...documents, newDocument]);
    }
  };

  const handleUpdateFile = (updatedAuditFile: Omit<Document, 'required' | 'name'>) => {
    const documents = [...(formik.values.documents as Omit<Document, 'required' | 'name'>[])];
    const index = documents.findIndex((file) => file.file.id === updatedAuditFile.file.id);
    documents[index] = updatedAuditFile;
    formik.setFieldValue('documents', documents);
  };

  const handleDeleteFile = (auditFile: Omit<Document, 'required' | 'name'>) => {
    const documents = [...(formik.values.documents as Omit<Document, 'required' | 'name'>[])];
    const index = documents.findIndex((file) => file.file.id === auditFile.file.id);
    documents.splice(index, 1);
    formik.setFieldValue('documents', documents);
  };

  const isFieldError = (field: string) => {
    return formik.touched[field] && formik.errors[field];
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <QStack spacing={4}>
        {Object.keys(formik.touched).length && Object.keys(formik.errors).length ? (
          <QAlert status="error" description={t('createAuditForm.errors.fillRequiredFields')} />
        ) : null}
        <QText weight="semibold">{t('createAuditForm.heading')}</QText>
        <QFormControl
          label={t('createAuditForm.fields.auditName.label')}
          isInvalid={isFieldError('auditName')}
          error={t('createAuditForm.fields.auditName.error')}
        >
          <QInput name="auditName" onChange={formik.handleChange} value={formik.values.auditName} />
        </QFormControl>
        <QFormControl
          label={t('createAuditForm.fields.auditType.label')}
          isInvalid={isFieldError('auditType')}
          error={t('createAuditForm.fields.auditType.error')}
          data-cy="audit-type"
        >
          <QSelect
            name="auditType"
            onChange={(e) => formik.setFieldValue('auditType', e)}
            value={formik.values.auditType.value}
            options={auditTypeOptions}
          />
        </QFormControl>
        <QFormControl label={t('createAuditForm.fields.notes.label')}>
          <QTextarea
            {...{ name: 'notes' }}
            onChange={(evt) => formik.handleChange({ ...evt, target: evt.currentTarget })}
            value={formik.values.notes}
          />
        </QFormControl>

        <QFormControl
          label={t('createAuditForm.fields.completionDate.label')}
          data-cy="end-date"
          helper={t('createAuditForm.fields.completionDate.helper')}
        >
          <QDatepicker
            onChange={(date: Date | null) => date && formik.setFieldValue('endDate', date)}
            value={formik.values.endDate}
            placeholder={t('createAuditForm.fields.completionDate.placeholder')}
            maxDate={new Date()}
          />
        </QFormControl>

        <QText weight="semibold" pt={3}>
          {t('createAuditForm.sections.auditDocuments')}
        </QText>
        {formik?.values?.documents?.map((document: Omit<Document, 'required' | 'name'>, index: number) => (
          <EditFileCard
            key={index}
            document={document}
            handleDeleteFile={() => handleDeleteFile(document)}
            handleUpdateFile={(updatedAuditFile) => handleUpdateFile(updatedAuditFile)}
          />
        ))}
        <AddDocumentCard
          handleDocumentAdded={handleAppendFile}
          setAddAuditSubmitButtonDisabled={setAddAuditSubmitButtonDisabled}
        />
      </QStack>
    </form>
  );
};

export default CreateAuditForm;
